/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 */
import React from 'react';
import logger from '../utils/logger';

// reCAPTCHA v3 utility functions
class RecaptchaV3Service {
  private static isLoaded = false;
  private static loadPromise: Promise<void> | null = null;

  static async load(): Promise<void> {
    if (this.isLoaded) return;
    if (this.loadPromise) return this.loadPromise;

    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      logger.warn('reCAPTCHA v3 site key not configured');
      return;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.grecaptcha) {
        this.isLoaded = true;
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.grecaptcha.ready(() => {
          this.isLoaded = true;
          resolve();
        });
      };

      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA v3'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  static async execute(action: string): Promise<string | null> {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      logger.warn('reCAPTCHA v3 site key not configured');
      return null;
    }

    try {
      await this.load();
      
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        throw new Error('reCAPTCHA v3 not properly loaded');
      }

      const token = await window.grecaptcha.execute(siteKey, { action });
      logger.info(`✅ reCAPTCHA v3 token generated for action: ${action}`);
      return token;
    } catch (error) {
      logger.error('❌ reCAPTCHA v3 execution failed:', error);
      return null;
    }
  }
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Hook for using reCAPTCHA v3
export const useRecaptchaV3 = () => {
  const executeRecaptcha = async (action: string): Promise<string | null> => {
    return RecaptchaV3Service.execute(action);
  };

  return { executeRecaptcha };
};

// Legacy component for backward compatibility (now invisible)
interface CaptchaComponentProps {
  onVerify: (token: string | null) => void;
  onExpired?: () => void;
  onError?: () => void;
  size?: 'compact' | 'normal';
  theme?: 'light' | 'dark';
}

const CaptchaComponent: React.FC<CaptchaComponentProps> = ({
  onVerify,
  onError
}) => {
  // v3 is invisible - no UI component needed
  // This component is kept for backward compatibility but does nothing
  // Use the useRecaptchaV3 hook instead for new implementations
  
  React.useEffect(() => {
    // Auto-execute on mount for backward compatibility
    const executeV3 = async () => {
      try {
        const token = await RecaptchaV3Service.execute('legacy');
        onVerify(token);
      } catch (error) {
        logger.error('reCAPTCHA v3 auto-execution failed:', error);
        onError?.();
      }
    };

    executeV3();
  }, [onVerify, onError]);

  // reCAPTCHA v3 is completely invisible
  return null;
};

export default CaptchaComponent; 